import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { Select, Typography, Spin } from '@/shared/ui/primitives';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { DefaultOptionType } from '@/shared/ui/subpath-types';

const { Text } = Typography;

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;
const SCROLL_THRESHOLD = 50;

export interface EntityFilterSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  queryKey: string[];
  fetchFn: (params: { search: string; page: number; size: number }) => Promise<{
    content: any[];
    totalPages: number;
    totalItems: number;
    currentPage: number;
  }>;
  /** When provided, used to resolve the display label of a value that isn't
   * in the loaded page (e.g. URL contains nationalityId=170 but the first
   * 20 sorted DESC are 250..231). Without this fetch the trigger renders
   * the raw ID instead of the human name. */
  fetchById?: (id: number) => Promise<any>;
  mapOption: (item: any) => DefaultOptionType;
  placeholder?: string;
}

export default function EntityFilterSelect({
  label,
  value,
  onChange,
  queryKey,
  fetchFn,
  fetchById,
  mapOption,
  placeholder,
}: EntityFilterSelectProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState<any[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchingRef = useRef(false);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [...queryKey, search, page],
    queryFn: () => fetchFn({ search, page, size: PAGE_SIZE }),
  });

  // Merge incoming data into accumulated list via useEffect (prevents infinite re-renders)
  useEffect(() => {
    if (!data?.content) return;
    if (page === 0) {
      setAccumulated(data.content);
    } else {
      setAccumulated((prev) => {
        const ids = new Set(prev.map((i: any) => i.id));
        const newItems = data.content.filter((i: any) => !ids.has(i.id));
        return newItems.length > 0 ? [...prev, ...newItems] : prev;
      });
    }
  }, [data?.content, page]);

  const hasMore = data ? data.currentPage < data.totalPages - 1 : false;

  // If `value` is set but the corresponding option isn't in the loaded page,
  // resolve it directly via `fetchById` so the Select trigger can render the
  // human name instead of the raw ID.
  const selectedQuery = useQuery({
    enabled: Boolean(value && fetchById) && !accumulated.some((i: any) => String(i.id) === String(value)),
    queryKey: [...queryKey, 'by-id', value],
    queryFn: () => fetchById!(Number(value)),
    staleTime: 5 * 60_000,
  });

  const options: DefaultOptionType[] = useMemo(() => {
    const base = accumulated.map((item) => normalizeOption(mapOption(item)));
    // Inject the resolved selected item if it isn't already in the list.
    if (selectedQuery.data && !base.some((o) => String(o.value) === String(value))) {
      return [normalizeOption(mapOption(selectedQuery.data)), ...base];
    }
    return base;
  }, [accumulated, mapOption, selectedQuery.data, value]);

  const loading = isLoading || isFetching;

  const handleSearch = useCallback((val: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      setPage(0);
      setAccumulated([]);
    }, DEBOUNCE_MS);
  }, []);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (!el) return;
    if (el.scrollTop + el.offsetHeight >= el.scrollHeight - SCROLL_THRESHOLD && hasMore && !isFetching && !fetchingRef.current) {
      fetchingRef.current = true;
      setPage((p) => p + 1);
      setTimeout(() => { fetchingRef.current = false; }, 200);
    }
  }, [hasMore, isFetching]);

  const dropdownRender = useCallback((menu: React.ReactNode) => (
    <>
      {menu}
      {loading && page > 0 && (
        <div style={{ textAlign: 'center', padding: 8 }}><Spin size="small" /></div>
      )}
    </>
  ), [loading, page]);

  return (
    <div style={{ minWidth: 200 }}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
        {label}
      </Text>
      <Select
        showSearch
        allowClear
        style={{ width: '100%' }}
        placeholder={placeholder ?? label}
        value={value || undefined}
        onChange={(v) => onChange(v != null ? String(v) : '')}
        onSearch={handleSearch}
        filterOption={false}
        loading={loading && page === 0}
        options={options}
        notFoundContent={loading ? t('common.loading') : t('common.noData')}
        onPopupScroll={handleScroll}
        popupRender={dropdownRender}
      />
    </div>
  );
}

function normalizeOption(option: DefaultOptionType): DefaultOptionType {
  if (option.value === undefined || option.value === null) return option;
  return { ...option, value: String(option.value) };
}
